import React, { ReactElement, ReactNode } from 'react';
import styled, { ColorsTypes, FontsWeights } from 'styled-components';
// import ReactIcon from '@mdi/react';
// import {
//   mdiClose,
//   mdiPlayCircleOutline,
//   mdiPlay,
//   mdiContentCopy,
//   mdiPencil,
//   mdiPlus,
//   mdiMagnify,
//   mdiViewDay,
//   mdiViewGrid,
//   mdiDrag,
//   mdiArrowLeft,
//   mdiArrowRight,
//   mdiEye,
//   mdiEyeOff,
//   mdiMenu,
//   mdiCubeOutline,
//   mdiLocationExit,
//   mdiCogOutline,
//   mdiText,
//   mdiFormatListNumbered,
//   mdiChevronUp,
//   mdiChevronDown,
//   mdiChevronLeft,
//   mdiChevronRight,
//   mdiViewDashboard,
//   mdiInvertColors,
//   mdiInvertColorsOff,
//   mdiFormatFontSizeIncrease,
//   mdiFormatFontSizeDecrease,
//   mdiFullscreen,
//   mdiFullscreenExit,
//   mdiInformationOutline,
//   mdiCardTextOutline,
//   mdiPlayBoxOutline,
//   mdiCheckboxOutline,
//   mdiLock,
//   mdiThumbDown,
//   mdiThumbUp,
//   mdiOpenInNew,
//   mdiFacebook,
//   mdiTwitter,
//   mdiLinkedin,
//   mdiInstagram,
//   mdiAccount,
// } from '@mdi/js';
import { space, border, flexbox, layout, position, SpaceProps, BorderProps, FlexboxProps, LayoutProps, PositionProps } from 'styled-system';

import { Badge, Flex, Icons } from 'components';
import { IconProps, IconsTypes } from 'components/icons';

// type ButtonIcons =
//   | 'account'
//   | 'close'
//   | 'playOutline'
//   | 'play'
//   | 'copy'
//   | 'edit'
//   | 'plus'
//   | 'search'
//   | 'listView'
//   | 'gridView'
//   | 'like'
//   | 'unlike'
//   | 'drag'
//   | 'lock'
//   | 'arrowLeft'
//   | 'arrowRight'
//   | 'eye'
//   | 'eyeOff'
//   | 'menu'
//   | 'cube'
//   | 'exit'
//   | 'gear'
//   | 'chevronUp'
//   | 'chevronDown'
//   | 'chevronLeft'
//   | 'chevronRight'
//   | 'text'
//   | 'quiz'
//   | 'dashboard'
//   | 'themeLight'
//   | 'themeDark'
//   | 'fontUp'
//   | 'fontDown'
//   | 'fullscreenOff'
//   | 'fullscreenOn'
//   | 'info'
//   | 'textOutline'
//   | 'playBoxOutline'
//   | 'checkboxOutline'
//   | 'openInNew'
//   | 'facebook'
//   | 'linkedin'
//   | 'twitter'
//   | 'instagram';

type ButtonSizes = 'small' | 'medium' | 'large';

type ButtonIconAllProps = React.ButtonHTMLAttributes<HTMLButtonElement> & SpaceProps & BorderProps & FlexboxProps & LayoutProps & PositionProps;

interface ButtonIconProps extends ButtonIconAllProps {
  icon: IconsTypes;
  size?: ButtonSizes;
  iconWidth?: { m?: number; t?: number; d?: number } | number;
  iconPosition?: 'before' | 'after';
  label?: string;
  color?: ColorsTypes;
  colorHover?: ColorsTypes;
  colorClick?: ColorsTypes;
  fontWeight?: FontsWeights;

  bg?: ColorsTypes | 'transparent';
  bgHover?: ColorsTypes | 'transparent';
  bgClick?: ColorsTypes | 'transparent';

  labelColor?: ColorsTypes;
  labelColorHover?: ColorsTypes;
  labelColorClick?: ColorsTypes;
  labelUnderline?: boolean;

  cursor?: 'pointer' | 'inherit';

  badge?: string | number | ReactNode;

  areaLabel?: string;
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  buttonSize: ButtonSizes;
  fontWeight: FontsWeights;
  iconWidth?: { m?: number; t?: number; d?: number } | number;
  iconPosition?: 'before' | 'after';
  iconPercentage: number;

  color: ColorsTypes;
  colorHover: ColorsTypes;
  colorClick: ColorsTypes;

  labelColor: ColorsTypes;
  labelColorHover: ColorsTypes;
  labelColorClick: ColorsTypes;
  labelUnderline: boolean;

  bg: ColorsTypes | 'transparent';
  bgHover: ColorsTypes | 'transparent';
  bgClick: ColorsTypes | 'transparent';
}

const Label = styled('div')`
  font-size: ${({ theme }) => theme.fontSizes[0]};
  text-transform: uppercase;
  transition: 0.2s;
  white-space: nowrap;
`;

const Icon = styled((props: IconProps) => <Icons {...props} />)`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: 0.2s;
`;

const IconWrapper = styled.div`
  display: inline-block;
  position: relative;
  justify-content: center;
`;

const Button = styled('button')<ButtonProps>`
  display: flex;
  align-items: center;
  border: 0;
  padding: 0;
  cursor: ${(props: any) => (props.disabled ? 'inherit' : props['data-cursor'])};
  color: ${({ theme, color }) => theme.colors[color]};
  background: ${({ theme, bg }) => (bg === 'transparent' ? 'transparent' : theme.colors[bg])};
  transition: 0.2s;
  outline: none;
  ${space}
  ${border}
  ${flexbox}
  ${layout}
  ${position}

  ${IconWrapper} {
    ${({ buttonSize, iconWidth }) => {
      if (iconWidth) {
        return `
          width: ${iconWidth}px;
          height: ${iconWidth}px;
        `;
      }

      switch (buttonSize) {
        case 'small':
          return `
            width: 14px;
            height: 14px;
          `;

        case 'large':
          return `
            width: 24px;
            height: 24px;
          `;

        default:
        case 'medium':
          return `
            width: 18px;
            height: 18px;
          `;
      }
    }}
  }

  ${Icon} {
    color: ${({ theme, color }) => theme.colors[color]};

    ${({ buttonSize, iconWidth, iconPercentage, theme: { mediaQueries } }) => {
      if (iconWidth) {
        if (typeof iconWidth === 'object') {
          let response = '';

          if (iconWidth.m) {
            response = `
              ${response}

              ${mediaQueries.mobile} {
                width: ${Math.ceil(iconWidth.m + (iconWidth.m / 100) * iconPercentage)}px;
                height: ${Math.ceil(iconWidth.m + (iconWidth.m / 100) * iconPercentage)}px;
              }
            `;
          }

          if (iconWidth.t) {
            response = `
              ${response}

              ${mediaQueries.tablet} {
                width: ${Math.ceil(iconWidth.t + (iconWidth.t / 100) * iconPercentage)}px;
                height: ${Math.ceil(iconWidth.t + (iconWidth.t / 100) * iconPercentage)}px;
              }
            `;
          }

          if (iconWidth.d) {
            response = `
              ${response}

              ${mediaQueries.desktop} {
                width: ${Math.ceil(iconWidth.d + (iconWidth.d / 100) * iconPercentage)}px;
                height: ${Math.ceil(iconWidth.d + (iconWidth.d / 100) * iconPercentage)}px;
              }
            `;
          }

          return `
              ${response}
            `;
        } else {
          return `
            width: ${Math.ceil(iconWidth + (iconWidth / 100) * iconPercentage)}px;
            height: ${Math.ceil(iconWidth + (iconWidth / 100) * iconPercentage)}px;
          `;
        }
      }

      switch (buttonSize) {
        case 'small':
          return `
            width: 19px;
            height: 19px;
          `;

        case 'large':
          return `
            width: 28px;
            height: 28px;
          `;

        default:
        case 'medium':
          return `
            width: 21px;
            height: 21px;
          `;
      }
    }}
  }

  ${Label} {
    color: ${({ theme, labelColor }) => theme.colors[labelColor]};
    font-weight: ${({ theme, fontWeight }) => theme.fontWeights[fontWeight]};
    text-decoration: ${({ labelUnderline }) => (labelUnderline ? 'underline' : 'none')};
    text-underline-offset: 3px;

    ${({ theme, iconPosition }) => {
      if (iconPosition === 'after') {
        return `
          padding-right: ${theme.space[2]}px
        `;
      }

      return `
        padding-left: ${theme.space[2]}px
      `;
    }}
  }

  &:hover {
    background: ${({ theme, bgHover }) => (bgHover === 'transparent' ? 'transparent' : theme.colors[bgHover])};

    ${Icon} {
      color: ${({ theme, colorHover }) => theme.colors[colorHover]};
    }

    ${Label} {
      color: ${({ theme, labelColorHover }) => theme.colors[labelColorHover]};
    }
  }

  &:active {
    background: ${({ theme, bgClick }) => (bgClick === 'transparent' ? 'transparent' : theme.colors[bgClick])};

    ${Icon} {
      color: ${({ theme, colorClick }) => theme.colors[colorClick]};
    }

    ${Label} {
      color: ${({ theme, labelColorClick }) => theme.colors[labelColorClick]};
    }
  }
`;

const ButtonIcon = ({
  label,
  size,
  iconWidth,
  color,
  colorHover,
  colorClick,
  labelColor,
  labelColorHover,
  labelColorClick,
  labelUnderline = false,
  bg,
  bgHover,
  bgClick,
  icon,
  fontWeight,
  cursor = 'pointer',
  areaLabel,
  iconPosition = 'before',
  badge,

  ...buttonProps
}: ButtonIconProps): ReactElement => {
  const buttonBg = bg || 'primary_400';
  const buttonBgHover = bgHover || (buttonBg === 'primary_400' ? 'primary_200' : buttonBg);
  const buttonBgClick = bgClick || (buttonBg === 'primary_400' ? 'primary_600' : buttonBg);

  const buttonColor = color || 'primary_contrast';
  const buttonColorHover =
    colorHover ||
    (buttonColor === 'primary_contrast' ? 'primary_contrast' : buttonColor) ||
    (buttonColor === 'primary_400' ? 'primary_200' : buttonColor);
  const buttonColorClick =
    colorClick ||
    (buttonColor === 'primary_contrast' ? 'primary_contrast' : buttonColor) ||
    (buttonColor === 'primary_400' ? 'primary_600' : buttonColor);

  const buttonLabelColor = labelColor || buttonColor;
  const buttonLabelColorHover = labelColorHover || buttonLabelColor;
  const buttonLabelColorClick = labelColorClick || buttonLabelColorHover;

  const buttonSize = size || 'medium';

  const buttonFontWeight = fontWeight || 'bold';

  let iconPercentage: number = 16.66;

  return (
    <Button
      data-cursor={cursor}
      color={buttonColor}
      colorHover={buttonColorHover}
      colorClick={buttonColorClick}
      buttonSize={buttonSize}
      iconWidth={iconWidth}
      iconPercentage={iconPercentage}
      bg={buttonBg}
      bgHover={buttonBgHover}
      bgClick={buttonBgClick}
      labelColor={buttonLabelColor}
      labelColorHover={buttonLabelColorHover}
      labelColorClick={buttonLabelColorClick}
      labelUnderline={labelUnderline}
      fontWeight={buttonFontWeight}
      aria-label={areaLabel}
      iconPosition={iconPosition}
      {...buttonProps}
    >
      <Flex alignItems="center" justifyContent="center" flexWrap="nowrap">
        {iconPosition === 'before' && (
          <IconWrapper>
            {!!badge && <Badge content={badge} />}

            <Icon icon={icon} />
          </IconWrapper>
        )}

        {Boolean(label) && <Label>{label}</Label>}

        {iconPosition === 'after' && (
          <IconWrapper>
            <Icon icon={icon} />
          </IconWrapper>
        )}
      </Flex>
    </Button>
  );
};
export default ButtonIcon;
